// frontend/src/lib/p2p/tokens.ts
import { uni2Chain } from '../../config/chains';
import { bsc } from 'wagmi/chains';

/**
 * Canonical type names for the P2P module. Re-exported by lib/api.ts so
 * both layers share a single source of truth.
 */
export type P2PChainName = 'UNI2' | 'BSC';
export type P2POrderSide = 'SELL_W2USDT' | 'SELL_USDT';
export type P2PFillMode = 'PARTIAL_OK' | 'AON';
export type P2POrderStatus = 'ACTIVE' | 'PARTIAL' | 'FILLED' | 'CANCELLED' | 'EXPIRED';
export type P2PTradeStatus = 'PENDING' | 'PARTIAL_SETTLED' | 'SETTLED' | 'FAILED';

/**
 * Map between user-facing P2P "side" and the chain + token that side
 * lives on. The escrow contract is single-asset per chain:
 *   - Uni2 chain: escrowed token = w2usdt
 *   - BSC chain:  escrowed token = USDT BEP20
 */
export interface ChainTokenInfo {
  chain: P2PChainName;
  chainId: number;
  tokenAddress: `0x${string}`;
  tokenSymbol: 'w2usdt' | 'USDT';
  decimals: number;
}

const UNI2_INFO: ChainTokenInfo = {
  chain: 'UNI2',
  chainId: 111888,
  tokenAddress: '0x4EB1f00B77a1C75F8597b581B21C90f713336A64',
  tokenSymbol: 'w2usdt',
  decimals: 18,
};

const BSC_INFO: ChainTokenInfo = {
  chain: 'BSC',
  chainId: 56,
  tokenAddress: '0x55d398326f99059fF775485246999027B3197955',
  tokenSymbol: 'USDT',
  decimals: 18,
};

/**
 * Escrow contract address per chain, sourced from build-time Vite env vars.
 * V2 deploys at different addresses on each chain (deployer nonce differs),
 * so this MUST NOT be a single global constant.
 *
 * Set in Coolify:
 *   VITE_P2P_ESCROW_ADDRESS_UNI2 = 0x736Ee0... (Uni2chain V2)
 *   VITE_P2P_ESCROW_ADDRESS_BSC  = 0x30CCC5... (BSC V2)
 *
 * Validation: if either var is missing or malformed at build time,
 * `escrowAddressForChain` throws — fail-fast prevents a quiet fallback
 * to the wrong network and a repeat of the V1 misrouting incident.
 */
const ESCROW_ADDRESSES: Record<P2PChainName, `0x${string}`> = (() => {
  const uni2 = import.meta.env.VITE_P2P_ESCROW_ADDRESS_UNI2 as string | undefined;
  const bsc = import.meta.env.VITE_P2P_ESCROW_ADDRESS_BSC as string | undefined;
  const ok = (v: unknown): v is `0x${string}` =>
    typeof v === 'string' && /^0x[a-fA-F0-9]{40}$/.test(v);
  if (!ok(uni2)) throw new Error('VITE_P2P_ESCROW_ADDRESS_UNI2 missing or invalid');
  if (!ok(bsc)) throw new Error('VITE_P2P_ESCROW_ADDRESS_BSC missing or invalid');
  return { UNI2: uni2, BSC: bsc };
})();

export function escrowAddressForChain(chain: P2PChainName): `0x${string}` {
  return ESCROW_ADDRESSES[chain];
}

/** Side → info about which chain+token gets LOCKED when the maker creates the order. */
export function infoForSide(side: P2POrderSide): ChainTokenInfo {
  return side === 'SELL_W2USDT' ? UNI2_INFO : BSC_INFO;
}

/** Side → info about the OPPOSITE chain+token (what the user receives). */
export function infoForOppositeSide(side: P2POrderSide): ChainTokenInfo {
  return side === 'SELL_W2USDT' ? BSC_INFO : UNI2_INFO;
}

/** P2P chain enum → wagmi chain config. */
export function wagmiChain(chain: P2PChainName) {
  return chain === 'UNI2' ? uni2Chain : bsc;
}

export const P2P_PAIR = 'W2USDT_USDT' as const;
